import { ChartColumnBig, CornerDownRight, Table2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SearchInput } from '@/components/custom/search-input';
import { Button } from '@/components/ui/button';
import { CohortBarChart, type CohortRow, type CohortSegment } from './charts/cohort-bar-chart';
import { SegmentedToggle } from './segmented-toggle';
import { type WidgetColumn, WidgetTable } from './widget-table';

type DrillView = 'cohorts' | 'table';

/** Rows per page in the drilldown's table view. */
const DRILL_PAGE_SIZE = 8;

export interface DrillTarget<T> {
  /** Grid key of the tile this drill was opened from — it renders directly beneath it. */
  source: string;
  /** Human name of that tile, shown in the panel's provenance line. */
  sourceLabel: string;
  title: string;
  /** One line of context: what this population is. */
  note?: string;
  rows: T[];
}

interface Props<T> {
  target: DrillTarget<T>;
  columns: WidgetColumn<T>[];
  /** Fields concatenated for the search box to match against. */
  searchText: (row: T) => string;
  /** Cohort breakdowns shown in the first view. */
  cohorts: (rows: T[]) => { title: string; rows: CohortRow[] }[];
  cohortSegments: CohortSegment[];
  /** Noun for the row count, e.g. "people". */
  unit?: string;
  onClose: () => void;
}

/**
 * The detail panel behind every tile.
 *
 * Rendered as a grid row **directly beneath the tile it came from** (see
 * `layoutWithDrill`) rather than as a modal, so the reader keeps the chart they
 * clicked in view while they read the detail.
 *
 * It has to be unmistakably *not another tile*. Rather than louder borders, it
 * inverts the page's figure/ground: tiles are white cards on the page
 * background, so the drilldown is a **recessed tray** (muted surface, inset
 * shadow) with its content on a card inside it. The title gets its own
 * treatment too — an eyebrow naming the source tile, then a larger heading —
 * where a tile title is small, semibold and grip-prefixed.
 *
 * Generic over the row type: columns and cohorts are supplied by the caller, so
 * CPR and EPR drill into their own entities without touching this file.
 */
export function DrillPanel<T>({
  target,
  columns,
  searchText,
  cohorts,
  cohortSegments,
  unit = 'rows',
  onClose,
}: Props<T>) {
  const [view, setView] = useState<DrillView>('cohorts');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return target.rows;
    return target.rows.filter((r) => searchText(r).toLowerCase().includes(needle));
  }, [target.rows, query, searchText]);

  return (
    <div
      data-dashboard-drill
      className="flex h-full min-h-0 flex-col gap-2.5 overflow-hidden rounded-xl bg-muted p-3.5 inset-shadow-sm ring-1 ring-border"
    >
      <header className="flex flex-wrap items-start justify-between gap-2 px-0.5">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.08em]">
            <CornerDownRight className="size-3.5" aria-hidden />
            Drilldown
            <span className="font-normal normal-case tracking-normal">
              from {target.sourceLabel}
            </span>
          </p>
          <h3 className="mt-0.5 truncate font-semibold text-lg leading-tight">{target.title}</h3>
          {target.note ? <p className="text-muted-foreground text-xs">{target.note}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SegmentedToggle
            label="Drilldown view"
            value={view}
            onChange={setView}
            // The tray is already `bg-muted`, so the control needs a lighter
            // track to stay legible against it.
            className="bg-background"
            options={[
              { value: 'cohorts', label: 'Cohorts', icon: ChartColumnBig },
              { value: 'table', label: 'Table', icon: Table2 },
            ]}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={onClose}
            aria-label="Close drilldown"
          >
            <X aria-hidden />
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <SearchInput
            value={query}
            onValueChange={setQuery}
            placeholder="Filter…"
            className="w-56"
            inputClassName="h-8"
          />
          <span className="text-muted-foreground text-xs tabular-nums">
            {filtered.length} of {target.rows.length} {unit}
          </span>
        </div>

        {view === 'cohorts' ? (
          <div className="flex min-h-0 flex-1 flex-wrap gap-6 overflow-auto">
            {cohorts(filtered).map((c) => (
              <CohortBarChart
                key={c.title}
                title={c.title}
                rows={c.rows}
                segments={cohortSegments}
              />
            ))}
          </div>
        ) : (
          <WidgetTable
            rows={filtered}
            columns={columns}
            pageSize={DRILL_PAGE_SIZE}
            emptyMessage={`No ${unit} match this filter.`}
          />
        )}
      </div>
    </div>
  );
}
